"use client";

import { Grid, Heading } from "@highereducation/hen-ui";
import { CompactProductListSearchWrapper } from "@/components/CompactProductList";
import { cn } from "@/lib/utils";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import { SuperlativesModuleTestResult } from "@/monarch/rules/superlativesModule/types";
import type { ProductCollectionsProps } from "./types";
import { DEFAULT_CTA_LABEL, normalizeProductCollectionsData } from "./utils";
import useTranslations from "@/i18n/useTranslations";

export default function BaseProductCollections({
  location,
  className,
}: ProductCollectionsProps) {
  const t = useTranslations("Common");
  return (
    <MonarchDataConsumer<SuperlativesModuleTestResult>
      experimentKey="useSuperlativesModule"
      render={(testResult) => {
        const { data } = testResult;

        if (
          !data ||
          (location === "authx" && !data.displayOnAuthX) ||
          (location === "search-above" && data.searchLocation !== "above") ||
          (location === "search-middle" && data.searchLocation !== "middle") ||
          (location === "search-below" && data.searchLocation !== "below")
        ) {
          return null;
        }

        const { title, mobileDisplayLimit, numberOfProducts, collections } =
          normalizeProductCollectionsData(data);

        if (!collections.length) {
          return null;
        }

        return (
          <section className={cn("w-full", className)}>
            <Heading variant="h2" className="mt-0 xl:text-3xl">
              {title}
            </Heading>

            <Grid columns={1} columns-lg={3} className="gap-2.5">
              {collections.map((collection, index) => {
                const hideOnMobile = index >= mobileDisplayLimit;
                return (
                  <div
                    key={`${collection.title}-${collection.query}`}
                    className={hideOnMobile ? "hidden md:block" : undefined}
                  >
                    <CompactProductListSearchWrapper
                      query={collection.query}
                      title={collection.title}
                      numberOfHits={numberOfProducts}
                      ctaUrl={collection.ctaUrl}
                      ctaLabel={
                        collection.ctaLabel === DEFAULT_CTA_LABEL
                          ? t("seeAll")
                          : collection.ctaLabel
                      }
                      ctaHtmlId="cta-superlatives"
                      location="superlative-module"
                    />
                  </div>
                );
              })}
            </Grid>
          </section>
        );
      }}
    />
  );
}
